/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * NO DASHES IN AN AUTOMATED CASE NOTE (v1_922). Every note the platform writes
 * (server routes and the dashboard's own automated notes) passes through here on
 * its way to case_notes. Only three things change:
 *   an em or en dash between words        becomes ", " (a range of numbers, "10–24", becomes "10 to 24")
 *   an em or en dash, or "- ", opening a line is a bullet and is dropped
 *   a spaced hyphen (" - ") between words becomes ", "
 * A hyphen joining two words is never touched, so names and terms such as
 * "Smith-Jones", "re-sent" or a company's own name are stored exactly as written.
 * Model text, request item labels and third party text reach notes too, so a
 * literal fix alone could not hold the rule. A note the officer types in the
 * notes box is stored as typed (addCaseNote does not use this). The CommonJS
 * routes use the same rule from api/_case-note-cjs.js.
 */
export function noteText(s) {
  if (typeof s !== 'string' || !/[–—]|(^|\n)[ \t]*- |\s-\s/.test(s)) return s;
  return s
    .replace(/(\d)[ \t]*[–—][ \t]*(?=\d)/g, '$1 to ')
    .replace(/(^|\n)[ \t]*(?:[–—]|-(?=[ \t]))[ \t]*/g, '$1')
    .replace(/[ \t]*[–—][ \t]*/g, ', ')
    .replace(/(\S)[ \t]+-[ \t]+(?=\S)/g, '$1, ')
    .replace(/,[ \t]*,/g, ',')
    .replace(/,[ \t]*([.;:)])/g, '$1')
    .replace(/:[ \t]*,[ \t]*/g, ': ')
    .replace(/\([ \t]*,[ \t]*/g, '(')
    .replace(/^[ \t]*,[ \t]*/, '')
    .replace(/[ \t]+$/gm, '');
}
export function cleanNoteRow(row) {
  return row && typeof row === 'object' && typeof row.content === 'string' ? { ...row, content: noteText(row.content) } : row;
}
// One row or several (a supabase insert).
export function cleanNoteRows(rows) {
  return Array.isArray(rows) ? rows.map(cleanNoteRow) : cleanNoteRow(rows);
}

// NO DASHES IN THE MODEL'S OFFICER FACING REVIEW (v1_968). The AI review and its
// Quick Update are model output shown to the officer (the review tiles, the
// verdict line, the board, the Audit Record). The adversarial review's prompt
// fixes its verdict to a short list, but Quick Update may reword it, and a walk
// caught "Approve - low residual risk" on the officer screen (w6640b142, step
// 17). Every string in the review goes through noteText, the rule notes use.
// Anything without a dash comes back as the same object, so a clean review is
// untouched and nothing re renders for it.
export function cleanModelText(v) {
  if (typeof v === 'string') return noteText(v);
  if (Array.isArray(v)) {
    let changed = false;
    const out = v.map(x => { const y = cleanModelText(x); if (y !== x) changed = true; return y; });
    return changed ? out : v;
  }
  if (v && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype) {
    let out = null;
    for (const k of Object.keys(v)) {
      const y = cleanModelText(v[k]);
      if (y !== v[k]) { if (!out) out = { ...v }; out[k] = y; }
    }
    return out || v;
  }
  return v;
}

// A HYPHEN JOINING TWO LOWERCASE WORDS IN MODEL PROSE BECOMES A SPACE (29/10/2026,
// owner). noteText keeps a hyphen joining two words, so model text such as
// "co-founded" reached the source of wealth note (probe ws7b7158b9, S5d). In model
// prose only, a hyphen joining two words becomes a space when the word after it
// starts in lowercase ("co-founded" to "co founded", "40-year-old" to "40 year
// old", and "Well-evidenced" at the start of a sentence to "Well evidenced"). A
// name has a capital after the hyphen and is kept ("Smith-Jones", "Rolls-Royce");
// a hyphen between two numbers is a range and reads "to" ("2011-2016" to "2011 to
// 2016"), as noteText already does for an en dash between numbers; a token
// holding "@" or "/" (an email, a link) or a time ("10:30-11:00") is left alone.
// Not used by cleanModelText, which walks whole objects holding ids and links.
export function modelProse(s) {
  if (typeof s !== 'string' || !s.includes('-')) return noteText(s);
  const out = s.split(/(\s+)/).map((tok) => {
    if (!tok.includes('-') || /[@/]|\d:\d/.test(tok)) return tok;
    return tok.replace(/([A-Za-z0-9]+)-(?=([A-Za-z0-9]+))/g, (m, left, right) =>
      /^[A-Z]/.test(right) ? m : (/^\d+$/.test(left) && /^\d+$/.test(right)) ? left + ' to ' : left + ' ');
  }).join('');
  return noteText(out);
}
